import { configureStore, Middleware } from "@reduxjs/toolkit";
import { apiSlice } from "@/redux/apiSlice";
import authReducer, { logout } from "@/redux/authSlice";
import counterReducer from "@/redux/counterSlice";
import invoiceReducer from "@/redux/invoiceSlice";
import sidePanelReducer from "@/redux/sidePanelSlice";
import walletReducer from "@/redux/walletRefundSlice";

/**
 * Drop every cached API response when the user logs out, whichever code path dispatches
 * `logout`, so the next user in this tab never sees the previous user's data (lists are scoped
 * per staff member). Lives here because apiSlice and authSlice import each other.
 */
const clearApiCacheOnLogout: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  if (logout.match(action)) {
    api.dispatch(apiSlice.util.resetApiState());
  }
  return result;
};

const store = configureStore({
  reducer: {
    auth: authReducer,
    counter: counterReducer,
    invoice: invoiceReducer,
    wallet: walletReducer,
    sidePanel: sidePanelReducer,
    [apiSlice.reducerPath]: apiSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      apiSlice.middleware,
      clearApiCacheOnLogout,
    ),
});

export type AppStore = typeof store;
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];

export default store;
